import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Clock, RefreshCw, Trash2 } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { formatDate } from '@/utils';

interface Conversation {
  id: string;
  other_user_id: string;
  other_username: string;
  other_avatar?: string;
  listing_title?: string;
  last_message?: string;
  last_message_at: string;
  unread_count: number;
}

export default function MessagesPage() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchConversations = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    else setIsRefreshing(true);
    try {
      const response = await apiClient.get<any>('/conversations');
      setConversations(response.data ?? []);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
    // Poll every 30 seconds
    const interval = setInterval(() => fetchConversations(true), 30000);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  const handleDeleteConversation = async (e: React.MouseEvent, convId: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Delete this conversation? This cannot be undone.')) return;
    try {
      await apiClient.delete(`/conversations/${convId}`);
      setConversations((prev) => prev.filter((c) => c.id !== convId));
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    }
  };

  const totalUnread = conversations.reduce((sum, c) => sum + (c.unread_count ?? 0), 0);

  return (
    <div className="bg-white dark:bg-zinc-900 min-h-screen">
      <div className="bg-gradient-to-r from-brand-600 to-accent-600 text-white py-10">
        <div className="container-custom flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-display font-bold mb-1">Messages</h1>
            <p className="text-brand-100">
              {totalUnread > 0 ? `${totalUnread} unread message${totalUnread !== 1 ? 's' : ''}` : 'Your conversations'}
            </p>
          </div>
          <button
            onClick={() => fetchConversations(true)}
            disabled={isRefreshing}
            className="flex items-center gap-2 bg-white dark:bg-zinc-900/20 hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900/30 text-white px-4 py-2 rounded-xl text-sm font-medium transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="container-custom py-8 max-w-3xl">
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => <div key={i} className="skeleton h-20 rounded-xl" />)}
          </div>
        ) : conversations.length === 0 ? (
          <div className="text-center py-24">
            <MessageCircle className="h-16 w-16 mx-auto mb-4 text-gray-400 dark:text-zinc-500" />
            <h3 className="text-xl font-bold text-gray-700 dark:text-zinc-300 mb-2">No messages yet</h3>
            <p className="text-gray-500 dark:text-zinc-500 mb-6">Start a conversation by clicking "Message Seller" on any listing</p>
            <Link to="/marketplace" className="btn-primary">Browse Listings</Link>
          </div>
        ) : (
          <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm overflow-hidden divide-y divide-gray-200 dark:divide-zinc-800">
            {conversations.map((conv) => (
              <Link
                key={conv.id}
                to={`/messages/${conv.id}`}
                className="flex items-center gap-4 p-4 hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900 transition-colors group"
              >
                {/* Avatar */}
                <div className="relative flex-shrink-0">
                  <div className="h-12 w-12 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-600 dark:text-brand-400 flex items-center justify-center font-bold text-lg">
                    {conv.other_avatar ? (
                      <img src={conv.other_avatar} alt={conv.other_username} className="h-full w-full rounded-full object-cover" />
                    ) : (
                      conv.other_username?.[0]?.toUpperCase()
                    )}
                  </div>
                  {conv.unread_count > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 bg-brand-600 text-white text-xs rounded-full flex items-center justify-center font-bold">
                      {conv.unread_count > 9 ? '9+' : conv.unread_count}
                    </span>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-0.5">
                    <p className={`font-semibold text-sm ${conv.unread_count > 0 ? 'text-gray-900 dark:text-zinc-100' : 'text-gray-700 dark:text-zinc-300'}`}>
                      {conv.other_username}
                    </p>
                    <span className="text-xs text-gray-500 dark:text-zinc-500 flex items-center gap-1 flex-shrink-0 ml-2">
                      <Clock className="h-3 w-3" />
                      {formatDate(conv.last_message_at, 'MMM d')}
                    </span>
                  </div>
                  {conv.listing_title && (
                    <p className="text-xs text-brand-600 dark:text-brand-400 font-medium truncate mb-0.5">Re: {conv.listing_title}</p>
                  )}
                  <p className={`text-sm truncate ${conv.unread_count > 0 ? 'text-gray-800 dark:text-zinc-200 font-medium' : 'text-gray-500 dark:text-zinc-500'}`}>
                    {conv.last_message ?? 'Start of conversation'}
                  </p>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {conv.unread_count > 0 && (
                    <div className="h-2.5 w-2.5 bg-brand-600 rounded-full" />
                  )}
                  <button
                    onClick={(e) => handleDeleteConversation(e, conv.id)}
                    className="opacity-0 group-hover:opacity-100 text-gray-400 dark:text-zinc-500 hover:text-red-400 transition-all p-1"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}